/**
 * Payment Processor Types
 *
 * Shared types for multi-processor payment support.
 *
 * Supported processors:
 * - USAePay: Legacy payment processor (Lambda-based)
 * - Dejavoo Spin: Physical terminals via cloud API (Fargate-based)
 *
 * Dejavoo Spin API Reference: https://app.theneo.io/dejavoo/spin/spin-rest-api-methods
 */

import { PaymentTypePatient } from './PatientPayment.type';

// ============================================================================
// Processor Types and Enums
// ============================================================================

export enum PaymentProcessorType {
  USAePay = 'usaepay',
  Dejavoo = 'dejavoo',
}

export enum DejavooTransactionType {
  Sale = 'sale',
  Auth = 'auth',
  Capture = 'capture',
  Void = 'void',
  Return = 'return',
}

export enum DejavooTransactionStatus {
  Pending = 'pending',
  SentToDevice = 'sent_to_device',
  Processing = 'processing',
  Approved = 'approved',
  Declined = 'declined',
  Error = 'error',
  Timeout = 'timeout',
  Cancelled = 'cancelled',
}

export enum DejavooBatchStatus {
  Open = 'open',
  Closing = 'closing',
  Closed = 'closed',
  Error = 'error',
}

// Dejavoo result codes
export enum DejavooResultCode {
  Success = '0',
  TerminalError = '1',
  APIError = '2',
}

// Common Dejavoo status codes
export enum DejavooStatusCode {
  Approved = '0000',
  TerminalBusy = '1000',
  AuthFailed = '1009',
  MissingRefId = '1010',
  DuplicateRefId = '1011',
  NoOpenBatch = '1020',
  NotConnected = '2001',
  InvalidAuthKey = '2002',
  RegisterNotFound = '2003',
  Timeout = '2007',
  TerminalInUse = '2008',
}

// ============================================================================
// Credential Types
// ============================================================================

export interface DejavooCredentials {
  tpn: string; // Terminal Profile Number (10-12 digits)
  authKey: string; // 10-character authentication key (found in terminal settings)
  registerId?: string; // (Obsolete) Not used in API calls, kept for backwards compatibility
  ecommToken?: string; // JWT token for iPOSpays Freedom to Design (manual entry) - generated in merchant portal
}

export interface PaymentProcessorConfigRow {
  id: number;
  clinicId: number;
  locationId?: number | null;
  processor: PaymentProcessorType;
  isActive: boolean;
  isDefault: boolean;
  config: Record<string, unknown> | null;
  createdAt: number;
  updatedAt?: number | null;
}

export interface PaymentProcessorDeviceRow {
  id: number;
  configId: number;
  credentialId?: number | null;
  processor: PaymentProcessorType;
  deviceKey: string;
  deviceName: string;
  deviceType?: string | null;
  status?: string | null;
  locationId?: number | null;
  deviceData?: Record<string, unknown> | null;
  isActive: boolean;
  createdAt: number;
  updatedAt?: number | null;
}

// ============================================================================
// Dejavoo API Request Types
// ============================================================================

export interface DejavooBaseRequest {
  TPN: string;
  AuthKey: string;
  RegisterId?: string;
}

export interface DejavooSaleRequest extends DejavooBaseRequest {
  Amount: string; // Format: "XX.XX"
  TipAmount?: string;
  InvoiceNumber?: string;
  RefId: string; // Unique transaction reference (1-50 chars)
  PaymentType?: DejavooPaymentType;
  PrintReceipt?: '0' | '1' | 'No' | 'Both' | 'Merchant' | 'Customer';
  SigCapture?: '0' | '1';
  GetExtendedData?: '1' | '0';
  SPInProxyTimeout?: number; // 1-720 seconds
  CallbackInfo?: DejavooCallbackInfo;
  CustomFields?: Record<string, string>;
  MerchantNumber?: string; // For multi-merchant environments (1-5)
}

export interface DejavooVoidRequest extends DejavooBaseRequest {
  RefId: string;
}

export interface DejavooReturnRequest extends DejavooBaseRequest {
  Amount: string;
  RefId: string;
  OriginalRefId?: string; // Reference to original transaction
}

export interface DejavooStatusRequest extends DejavooBaseRequest {
  RefId: string;
}

export interface DejavooSettleRequest extends DejavooBaseRequest {
  SettleType?: 'Close' | 'Force';
}

export interface DejavooAbortRequest extends DejavooBaseRequest {
  RefId?: string;
}

export interface DejavooCallbackInfo {
  Url: string;
  Headers?: Record<string, string>;
}

export type DejavooPaymentType =
  | 'Credit'
  | 'Debit'
  | 'EBT_Food'
  | 'EBT_Cash'
  | 'Card'
  | 'Cash'
  | 'Check'
  | 'Gift'
  | 'userChoice';

// ============================================================================
// Dejavoo API Response Types
// ============================================================================

export interface DejavooGeneralResponse {
  ResultCode: string; // '0' = Success, '1' = Terminal Error, '2' = API Error
  StatusCode: string; // 4-digit code (0000 = Approved)
  Message?: string;
  DetailedMessage?: string;
  DelayBeforeNextRequest?: number;
}

export interface DejavooTransactionResponse extends DejavooGeneralResponse {
  AuthCode?: string;
  ReferenceId?: string; // SPIn transaction identifier
  RefId?: string; // Echo of request RefId
  SerialNumber?: string;
  BatchNumber?: string;
  TransactionNumber?: string;
  Voided?: boolean;

  // Card data
  CardNumber?: string; // Masked (last 4)
  CardType?: string; // Visa, MC, Amex, etc.
  EntryMethod?: string; // Swipe, Chip, Contactless, Manual
  CardholderName?: string;

  // Amounts
  Amount?: string;
  TipAmount?: string;
  TotalAmount?: string;

  // Tokenization
  IPosToken?: string; // Token for future transactions

  // EMV data
  EMVData?: {
    AID?: string;
    TVR?: string;
    TSI?: string;
    IAD?: string;
    ARC?: string;
  };

  // Receipts
  CustomerReceipt?: string;
  MerchantReceipt?: string;

  // Extended data
  ExtendedDataByApplication?: Record<string, unknown>;
}

export interface DejavooSettleResponse extends DejavooGeneralResponse {
  BatchId?: string;
  BatchNumber?: string;
  TransactionCount?: number;
  TotalAmount?: string;
}

export interface DejavooTerminalStatusResponse extends DejavooGeneralResponse {
  IsConnected?: boolean;
  TerminalStatus?: string;
  SerialNumber?: string;
  FirmwareVersion?: string;
}

// ============================================================================
// Internal Application Types
// ============================================================================

export interface DejavooTransactionRecord {
  id: number;
  clinicId: number;
  locationId?: number | null;
  patientId: string;
  billingKey?: string | null;
  tpn: string;
  registerId?: string | null;
  referenceId?: string | null;
  authCode?: string | null;
  transactionType: DejavooTransactionType;
  amount: number;
  tipAmount?: number;
  status: DejavooTransactionStatus;
  resultCode?: string | null;
  statusCode?: string | null;
  errorMessage?: string | null;
  iPosToken?: string | null;
  cardLastFour?: string | null;
  cardType?: string | null;
  cardholderName?: string | null;
  requestData?: Record<string, unknown> | null;
  responseData?: DejavooTransactionResponse | null;
  batchId?: string | null;
  settledAt?: number | null;
  originalTransactionId?: number | null;
  refunded: boolean;
  voided: boolean;
  reconciling?: string | null;
  createdAt: number;
  updatedAt?: number | null;
  completedAt?: number | null;
}

export interface DejavooBatchRecord {
  id: number;
  clinicId: number;
  locationId?: number | null;
  tpn: string;
  batchId: string;
  batchNumber?: string | null;
  status: DejavooBatchStatus;
  transactionCount: number;
  salesAmount: number;
  refundsAmount: number;
  netAmount: number;
  openedAt: number;
  closedAt?: number | null;
}

export interface DejavooCustomerTokenRecord {
  id: number;
  clinicId: number;
  patientId: string;
  credentialId: number;
  iPosToken: string;
  cardLastFour: string;
  cardType?: string | null;
  cardholderName?: string | null;
  expirationMonth?: string | null;
  expirationYear?: string | null;
  nickname?: string | null;
  isDefault: boolean;
  // When true, the credit-card processing fee is waived on transactions where
  // this card is selected. See `docs` / migration
  // 20260508120000_add_suppress_processing_fee.sql for context.
  suppressProcessingFee: boolean;
  isActive: boolean;
  createdAt: number;
  updatedAt?: number | null;
  lastUsedAt?: number | null;
  consumerId?: string | null;
  suppressProcessingFee?: boolean;
}

// ============================================================================
// Payment Request/Response Types (Processor-agnostic)
// ============================================================================

export interface PaymentSaleRequest {
  clinicId: number;
  locationId?: number;
  patientId: string;
  amount: number;
  tipAmount?: number;
  deviceKey?: string; // For terminal payments
  tokenId?: number; // For stored card payments
  invoiceNumber?: string;
  saveCard?: boolean;
  printReceipt?: boolean;
  captureSignature?: boolean;
  payment?: Record<string, unknown>; // Additional payment context
}

export interface PaymentSaleResponse {
  success: boolean;
  transactionId: number;
  processor: PaymentProcessorType;
  referenceId?: string;
  authCode?: string;
  status: DejavooTransactionStatus | string;
  errorMessage?: string;
  cardLastFour?: string;
  cardType?: string;
  token?: string; // If card was tokenized
  amount: number;
  tipAmount?: number;
  billingKey?: string;
}

export interface PaymentRefundRequest {
  clinicId: number;
  transactionId: number;
  amount?: number; // Partial refund amount (full refund if not specified)
  reason?: string;
  refundMethod?: PaymentTypePatient;
  refundProcessingFee?: boolean;
}

export interface PaymentRefundResponse {
  success: boolean;
  transactionId: number;
  referenceId?: string;
  status: string;
  errorMessage?: string;
  amount: number;
}

export interface PaymentVoidRequest {
  clinicId: number;
  transactionId: number;
  reason?: string;
}

export interface PaymentVoidResponse {
  success: boolean;
  transactionId: number;
  referenceId?: string;
  status: string;
  errorMessage?: string;
}

export interface BatchSettleRequest {
  clinicId: number;
  tpn: string;
  forceSettle?: boolean;
}

export interface BatchSettleResponse {
  success: boolean;
  batchId?: string;
  batchNumber?: string;
  transactionCount?: number;
  totalAmount?: number;
  errorMessage?: string;
}
